/* eslint-disable max-lines-per-function */
import React from "react";
import MaterialTable, { Column, MTableToolbar } from "@material-table/core";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gql } from "graphql-tag";
import moment, { Moment } from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { DatePicker } from "@mui/x-date-pickers";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { invoicedTrackingQueries, invoicedTrackingQueries_invoicedTrackings as invoicedTrackings, RelatedEntityType, sendTrackingToInvoiced } from "types/gql-types";
import { Button, LinkButton } from "components/haven-design";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { sendableIds, sendSummary, stillSending } from "./send-progress";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_MS = 5 * 60 * 1000;

const BILLING_BY_MONTH = gql`
  query invoicedTrackingQueries($month: Date!) {
    invoicedTrackings(month: $month) {
      id
      month
      regularQuantity
      overageQuantity
      sentToInvoiced
      invoicedSendError
      queuedForInvoiced
      invoicedCustomer {
        relatedEntityType
        property {
          id
          name
          organization {
            id
            name
          }
        }
        organization {
          id
          name
        }
      }
    }
  }
`;

const SEND_TO_INVOICED = gql`
  mutation sendTrackingToInvoiced($trackingIds: [ID!]!) {
    sendTrackingToInvoiced(trackingIds: $trackingIds)
  }
`;

const getOrg = ({ invoicedCustomer }:invoicedTrackings) =>
  invoicedCustomer.organization ?? invoicedCustomer.property?.organization;

const renderOrgColumn =
  (invoicedTracking:invoicedTrackings) => {
    const { month } = invoicedTracking;
    const org = getOrg(invoicedTracking);

    if (!org) {
      return null;
    }

    return <Link to={`/dash/admin/billing/letters/o/${org.id}/${moment(month).format("MM-YYYY")}`}>{org.name}</Link>;
  };

const renderPropColumn =
    ({ month, invoicedCustomer: { property, relatedEntityType } }:invoicedTrackings) => {
      if (relatedEntityType === RelatedEntityType.ORGANIZATION) {
        return <em>all properties</em>;
      }

      return <Link to={`/dash/admin/billing/letters/p/${property?.id}/${moment(month).format("MM-YYYY")}`}>{property?.name}</Link>;
    };

const columns: Array<Column<invoicedTrackings>> = [
  { title: "Month", field: "month", render: ({ month }) => moment(month).format("MM/YYYY") },
  { title: "Organization",
    field: "invoicedCustomer.property.organization.name",
    render: renderOrgColumn },
  { title: "Property", field: "invoicedCustomer.property.name", render: renderPropColumn },
  { title: "Sent to Invoiced", field: "sentToInvoiced", type: "boolean" },
  { title: "Send Error", field: "invoicedSendError" },
  { title: "Letters", field: "regularQuantity" },
  { title: "Additional Pages", field: "overageQuantity" },
];

const filterAndSortTableData = (data:invoicedTrackingQueries | undefined, showZeroes:boolean) => {
  if (!data || !data.invoicedTrackings) return [];

  const filterZeroes = (tracking: invoicedTrackings) =>
    tracking.overageQuantity !== 0 || tracking.regularQuantity !== 0;

  const resultSet =
    showZeroes ? data.invoicedTrackings : data.invoicedTrackings.filter(filterZeroes);

  return resultSet
    .sort((a, b) => {
      if (a.sentToInvoiced !== b.sentToInvoiced) return a.sentToInvoiced ? -1 : 1;

      if (getOrg(a)?.name !== getOrg(b)?.name) return getOrg(a)?.name.localeCompare(getOrg(b)?.name ?? "") ?? 0;

      return a.invoicedCustomer.property?.name.localeCompare(b.invoicedCustomer.property?.name ?? "") ?? 0;
    });
};

const navigateToMonth = (
  navigateFn: ReturnType<typeof useNavigate>,
  month?: Date | Moment | null,
) => {
  const monthString = moment(month).startOf("month").format("MM-YYYY");
  navigateFn(`/dash/admin/billing/${monthString}`);
};

const BillingSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { month: monthParam } = useParams() as { month?: string };

  React.useEffect(() => {
    if (!monthParam) {
      navigateToMonth(navigate);
    }
  }, [monthParam, navigate]);

  const [month, setMonth] = React.useState(
    monthParam ? moment(monthParam, "MM-YYYY").startOf("month") : moment().startOf("month"),
  );
  const [showZeroes, setShowZeroes] = React.useState(false);
  // Records queued for the background send; the table polls until each is sent or failed.
  const [batch, setBatch] = React.useState<{ ids: string[]; startedAt: number } | null>(null);
  const { data, error, loading, refetch } =
    useQuery<invoicedTrackingQueries>(BILLING_BY_MONTH, {
      variables: { month: month.format("YYYY-MM-DD") },
      fetchPolicy: "no-cache",
      pollInterval: batch ? POLL_INTERVAL_MS : 0,
    });
  const [sendToInvoiced, { loading: sending }] =
    useMutation<sendTrackingToInvoiced>(SEND_TO_INVOICED);
  // Guards double clicks that land before `sending` re-renders the button disabled.
  const sendingRef = React.useRef(false);
  const { openSnackbar } = useSnackbar();

  React.useEffect(() => {
    if (!batch || !data?.invoicedTrackings) return;

    const pending = stillSending(batch.ids, data.invoicedTrackings);
    if (pending.length > 0 && Date.now() - batch.startedAt < MAX_POLL_MS) return;

    openSnackbar(sendSummary(batch.ids, data.invoicedTrackings));
    setBatch(null);
  }, [batch, data, openSnackbar]);

  const sendUnsent = () => {
    if (sendingRef.current || batch) return;
    if (!data || !data.invoicedTrackings || data.invoicedTrackings.length === 0) return;

    const trackingIds = sendableIds(data.invoicedTrackings);

    if (trackingIds.length === 0) return;

    sendingRef.current = true;
    sendToInvoiced({ variables: { trackingIds } })
      .then(() => {
        openSnackbar({ message: `Sending ${trackingIds.length} to Invoiced. This page updates as they finish.` });
        // Wait for data that shows the rows as queued before judging which records finished.
        return refetch().then(() => setBatch({ ids: trackingIds, startedAt: Date.now() }));
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Error sending items to Invoiced.", status: "error" });
        refetch();
      })
      .finally(() => {
        sendingRef.current = false;
      });
  };

  if (error) return <span>Server Error</span>;

  const tableData: invoicedTrackings[] = filterAndSortTableData(data, showZeroes);

  const hasUnsent = sendableIds(data?.invoicedTrackings ?? []).length > 0;

  return <MaterialTable
    title="Billing"
    data={tableData}
    columns={columns}
    isLoading={loading}
    options={{ pageSize: 50 }}

    components={{ Toolbar: function Toolbar (props) {
      return (
        <><div>
          <MTableToolbar {...props} />
        </div>
        <Grid container style={{ paddingLeft: 10 }}>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              slotProps={{
                textField: {
                  label: "Month",
                },
              }}
              value={month.toDate()}
              // Switching months mid-batch would hide the rows being tracked.
              disabled={sending || Boolean(batch)}
              format="MM/yyyy"
              openTo="month"
              views={["year", "month"]}
              closeOnSelect
              onChange={(date: Date | null) => {
                if (date instanceof Date && !isNaN(date.getTime())) {
                  setMonth(moment(date).startOf("month"));
                  navigateToMonth(navigate, date);
                }
              }} />
            <FormControlLabel
              style={{ paddingTop: 8, paddingLeft: 16 }}
              control={<Checkbox checked={showZeroes}
                onClick={() => setShowZeroes(!showZeroes)} />}
              label="Include Zeroes" />
          </Grid>
          <Grid container size={{ xs: 6 }} justifyContent="flex-end" spacing={2}>
            <Grid>
              <LinkButton color="secondary" to={`/dash/admin/billing/new/${moment(month).startOf("month").format("MM-YYYY")}`}>
                New Monthly Billings
              </LinkButton>
            </Grid>
            <Grid>
              <Button
                disabled={!hasUnsent || sending || Boolean(batch)}
                onClick={() => sendUnsent()} color="primary">
                {sending || batch ? "Sending to Invoiced…" : "Send Unsent to Invoiced"}
              </Button>
            </Grid>
          </Grid>
        </Grid></>
      );
    } }}
  />;
};

export default BillingSearchPage;
